<script setup lang="ts">
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardPlus,
  User,
  XCircle
} from 'lucide-vue-next';
import { ref } from 'vue';
import ReqRespPreviewSheet from './ReqRespPreviewSheet.vue';


interface Props {
  response: any[];
}


const props = defineProps<Props>()

const isSheetOpen = ref(false)

const jsonItem = ref(false)

const handleViewSuccessJson = (item: any) => {
  console.log(`Item: ${item}`);
  jsonItem.value  = item;
  isSheetOpen.value = true;

}

const handleViewRequest = (item: any) => {
  console.log(`Item: ${item}`);
  jsonItem.value  = item;
  isSheetOpen.value = true;

}

// Check if response contains error/failure
const isFailureResponse = (item: any) => {
  return item.status === 'FAILURE' || item.status_code >= 400
}

// Get status badge variant
const getStatusVariant = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
      return 'default'
    case 'FAILURE':
      return 'destructive'
    case 'PENDING':
      return 'secondary'
    default:
      return 'outline'
  }
}

// Format date
const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A'
  return new Date(dateStr.replace(/\//g, '-')).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}
</script>

<template>
  <div class="space-y-6">
    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card>
        <CardHeader class="pb-2">
          <CardDescription>Total Processed</CardDescription>
          <CardTitle class="text-3xl">{{ response.length }}</CardTitle>
        </CardHeader>
      </Card>
      
      <Card>
        <CardHeader class="pb-2">
          <CardDescription>Successful</CardDescription>
          <CardTitle class="text-3xl text-green-600">
            {{ response.filter(r => !isFailureResponse(r)).length }}
          </CardTitle>
        </CardHeader>
      </Card>
      
      <Card>
        <CardHeader class="pb-2">
          <CardDescription>Failed</CardDescription>
          <CardTitle class="text-3xl text-red-600">
            {{ response.filter(r => isFailureResponse(r)).length }}
          </CardTitle>
        </CardHeader>
      </Card>
    </div>

    <!-- Results List -->
    <div v-for="(item, index) in response" :key="index" class="space-y-4">
      <!-- Failure Response -->
      <Card v-if="isFailureResponse(item)" class="border-red-200 bg-red-50">
        <CardHeader>
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <XCircle class="h-6 w-6 text-red-600" />
              <div>
                <CardTitle class="text-lg text-red-900">Rollover Failed</CardTitle>
                <CardDescription class="text-red-700 mt-1">
                  {{ item.description || 'An error occurred during rollover' }}
                </CardDescription>
              </div>
            </div>
            <Badge :variant="getStatusVariant(item.status)">
              {{ item.status }} ({{ item.status_code }})
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent>
          <div class="bg-white rounded-lg p-4 space-y-3">
            <h4 class="font-semibold text-gray-900 mb-3">Member Information</h4>
            
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p class="text-xs text-gray-500">Scheme</p>
                <p class="font-medium">{{ item.membership?.scheme_code || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Option</p>
                <p class="font-medium">{{ item.membership?.option_code || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Membership Number</p>
                <p class="font-medium">{{ item.membership?.membership_number || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Dependant</p>
                <p class="font-medium">{{ item.membership?.dependant_code || 'N/A' }}</p>
              </div>
            </div>

            <div v-if="item.membership?.tariff_codes?.length" class="pt-3 border-t">
              <p class="text-xs text-gray-500 mb-2">Attempted Tariff Codes</p>
              <div class="flex flex-wrap gap-2">
                <Badge 
                  v-for="code in item.membership.tariff_codes" 
                  :key="code"
                  variant="outline"
                  class="bg-red-50"
                >
                  {{ code }}
                </Badge>
              </div>
            </div>
          </div>
          <CardFooter>
            <div class="flex justify-end items-center mt-4">
              <Button 
              size="lg" 
              @click="handleViewRequest(item)" 
              class="bg-[#cfb76f] border-none text-white py-1 px-3 rounded-md"
            >
              VIEW JSON REQUEST
            </Button>
            </div>
            
          </CardFooter>
        </CardContent>

      </Card>

      <!-- Success Response with Transaction -->
      <Card v-else-if="item.Transaction" class="border-green-200 bg-green-50 p-2">
        <CardHeader>
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <CheckCircle2 class="h-6 w-6 text-green-600" />
              <div>
                <CardTitle class="text-lg text-green-900">Rollover Successful</CardTitle>
                <CardDescription class="text-green-700 mt-1">
                  Transaction #{{ item.Transaction.TransactionHeader.TranNum }}
                </CardDescription>
              </div>
            </div>
            <Badge variant="default" class="bg-green-600">SUCCESS</Badge>
          </div>
        </CardHeader>
        
        <CardContent class="space-y-4">
          <!-- Transaction Header -->
          <div class="bg-white rounded-lg p-4">
            <h4 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div class="p-1 bg-slate-900 rounded-full">
                <ClipboardPlus color="white" class="h-4 w-4" />
              </div>
              Transaction Details
            </h4>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p class="text-xs text-gray-500">Transaction Number</p>
                <p class="font-medium">{{ item.Transaction.TransactionHeader.TranNum }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Transaction Date</p>
                <p class="font-medium">{{ item.Transaction.TransactionHeader.TranDate }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Destination</p>
                <p class="font-medium">{{ item.Transaction.TransactionHeader.DestinationIdentifier }}</p>
              </div>
            </div>
          </div>

          <!-- Member Details -->
          <div class="bg-white rounded-lg p-4">
            <h4 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div class="bg-slate-900 text-white rounded-full p-1">
                <User class="w-4 h-4" />
              </div>
              Member Information
            </h4>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p class="text-xs text-gray-500">Name</p>
                <p class="font-medium">
                  {{ item.Transaction.Authorisation.Firstnames }} 
                  {{ item.Transaction.Authorisation.Surname }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Membership</p>
                <p class="font-medium">{{ item.Transaction.Authorisation.MembershipNumber }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Scheme</p>
                <p class="font-medium">
                  {{ item.Transaction.Authorisation.Scheme }} - 
                  {{ item.Transaction.Authorisation.OptionCode }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Birth Date</p>
                <p class="font-medium">{{ formatDate(item.Transaction.Authorisation.BirthDate) }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Auth Seq</p>
                <p class="font-medium">{{ item.Transaction.Authorisation.AuthSeq }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Program</p>
                <p class="font-medium">{{ item.Transaction.Authorisation.Program }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">Auth Start Date</p>
                <p class="font-medium">{{ formatDate(item.Transaction.Authorisation.AuthorisationStartDate) }}</p>
              </div>
            </div>
          </div>

          <!-- Disease Information -->
          <div 
            v-if="item.Transaction.Authorisation.AuthorisationDisease?.length"
            class="bg-white rounded-lg p-4"
          >
            <h4 class="font-semibold text-gray-900 mb-3">Disease Information</h4>
            <div 
              v-for="disease in item.Transaction.Authorisation.AuthorisationDisease" 
              :key="disease.ChronicDiseaseSeq"
              class="flex items-center justify-between py-2 border-b last:border-b-0"
            >
              <div>
                <p class="font-medium">{{ disease.ChronicDisease }}</p>
                <p class="text-xs text-gray-500">Created: {{ formatDate(disease.DateCreated) }}</p>
              </div>
              <Badge :variant="disease.ActiveIndicator === 'Y' ? 'default' : 'secondary'" class="bg-slate-100 px-2">
                {{ disease.ActiveIndicator === 'Y' ? 'Active' : 'Inactive' }}
              </Badge>
            </div>
          </div>

          <!-- Group Details Accordion -->
          <div class="bg-white rounded-lg p-2">
            <div class="border-b-gray-100 border-b-2">
              <h4 class="font-semibold text-gray-900 mb-3">
                Tariff Codes ({{ item.Transaction.Authorisation.GroupDetails?.length || 0 }})
              </h4>
            </div>
            <Accordion type="single" collapsible class="w-full">
              <AccordionItem 
                v-for="(group, gIndex) in item.Transaction.Authorisation.GroupDetails" 
                :key="gIndex"
                :value="`group-${gIndex}`"
              >
                <AccordionTrigger class="hover:no-underline">
                  <div class="flex items-center justify-between w-full">
                    <div class="space-y-2">
                      <div 
                        v-for="charge in group.ChargeDetails" 
                        :key="charge.ChargeableCode"
                        class="p-3 rounded"
                      >
                        <div class="items-start">
                          <div class="flex space-x-12 mb-2">
                            <p class="font-medium">{{ charge.ChargeableCode }}</p>
                            <Badge variant="outline">
                            {{ group.GroupStartDate }} 
                            {{ group.GroupEndDate ? ` → ${group.GroupEndDate}` : ' → Ongoing' }}
                            </Badge>                            
                          </div>
                          <div>
                            <p class="text-xs font-light text-gray-600">{{ charge.Description }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                     <div class="text-left">
                      <p class="font-medium">{{ group.GroupTypeDescription }}</p>
                    </div>
                    <div class="flex items-center gap-4">
                      <Badge variant="outline">
                        {{ group.UsedQuantity }}/{{ group.AllowedQuantity }}
                      </Badge>
                      <Badge :variant="group.LineStatus === 'ACTIVE' ? 'default' : 'secondary'">
                        {{ group.LineStatus }}
                      </Badge>
                    </div>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent class="space-y-4 pt-4">
                  <!-- Charge Details -->
                  <div v-if="group.ChargeDetails?.length">
                    <p class="text-sm font-semibold mb-2">Charge Details</p>
                    <div class="space-y-2">
                      <div 
                        v-for="charge in group.ChargeDetails" 
                        :key="charge.ChargeableCode"
                        class="bg-gray-50 p-3 rounded"
                      >
                        <div class="flex items-start justify-between">
                          <div>
                            <p class="font-medium">{{ charge.ChargeableCode }}</p>
                            <p class="text-sm text-gray-600">{{ charge.Description }}</p>
                          </div>
                          <Badge variant="outline">{{ charge.Publication }}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Speciality Details -->
                  <div v-if="group.SpecialityDetails?.length">
                    <p class="text-sm font-semibold mb-2">Specialities</p>
                    <div class="flex flex-wrap gap-2">
                      <Badge 
                        v-for="spec in group.SpecialityDetails" 
                        :key="spec.Speciality"
                        variant="secondary"
                      >
                        {{ spec.Speciality }}
                      </Badge>
                    </div>
                  </div>

                  <!-- Additional Info -->
                  <div class="grid grid-cols-2 gap-4 pt-2 border-t">
                    <div>
                      <p class="text-xs text-gray-500">Chronic Disease Seq</p>
                      <p class="text-sm font-medium">{{ group.ChronicDiseaseSeq }}</p>
                    </div>
                    <div>
                      <p class="text-xs text-gray-500">Group Type Seq</p>
                      <p class="text-sm font-medium">{{ group.GroupTypeSeq }}</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </CardContent>
        <CardFooter>
          <div class="flex items-center justify-end gap-3 p-2">
            <Button 
              size="lg" 
              @click="handleViewSuccessJson(item)" 
              class="bg-[#cfb76f] border-none text-white py-1 px-3 rounded-md"
            >
              VIEW JSON PAYLOAD
            </Button>
          </div>  
        </CardFooter>
      </Card>
    </div>

    <!-- Empty State -->
    <Card v-if="!response || response.length === 0">
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-gray-400 mb-4" />
        <p class="text-gray-500">No results to display</p>
      </CardContent>
    </Card>
  </div>
  <div>
    <div>
    <!-- Preview Results Dialog -->
    <ReqRespPreviewSheet
      v-if="jsonItem && isSheetOpen"
      v-model:open="isSheetOpen"
      :data="jsonItem"
    />
  </div>
  </div>
</template>